import { DownloadIcon } from '@phosphor-icons/react';
import { Agreement, AgreementTypeEnum } from '@sr-sdks/api-sdk-axios';
import { Button, Icon } from '@sr/ui-library';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AGREEMENTS_DEFAULT_PARAMS,
  useAgreements,
} from '../../../../api/services/agreementsApi';
import { StatusBadge } from '../../../../components/Badges/StatusBadge';
import DateFormatter from '../../../../components/DateFormatter';
import { AppDataTable } from '../../../../components/data-table';
import { LAST_SUCCESSFUL_DATA_SCOPES } from '../../../../constant/lastSuccessfulDataScopes';
import { useLastSuccessfulData } from '../../../../hooks/useLastSuccessfulData';
import { beneficiaryService } from '../../../../reducers/services/beneficiarySlice';
import { useTypedSelector } from '../../../../stateStore';
import downloadApi from '../../../../utils/api/downloadApi';
import { toTitleCase } from '../../../../utils/helpers/common';

type LoadingType = { agreementId: string; isLoading: boolean };

export const MarketingAgreementsDataTable = () => {
  const currentBeneficiary = useTypedSelector(
    beneficiaryService.selectors.currentBeneficiary,
  );

  const [downloadProps, setDownloadProps] = useState<LoadingType>();

  // Seeded from the URL on first render (not just `useState(1)`) so a deep
  // link like `?marketingAgreementsTablePage=3` fetches page 3 straight away
  // instead of requesting page 1 first and correcting itself once
  // DataTablePagination's mount effect reports the real URL page.
  const [searchParams] = useSearchParams();
  // Tracks the live sort so it can be passed back into AppDataTable for the
  // header's active/direction state — a hardcoded constant here would freeze
  // AppDataTable's toggle logic on its first direction forever.
  const [sortBy, setSortBy] = useState(
    () =>
      searchParams.get('marketingAgreementsTableSortBy') ??
      AGREEMENTS_DEFAULT_PARAMS.sortBy,
  );
  const [page, setPage] = useState(
    () =>
      Number(searchParams.get('marketingAgreementsTablePage')) ||
      AGREEMENTS_DEFAULT_PARAMS.page,
  );

  const queryParams = useMemo(() => {
    if (!currentBeneficiary) return undefined;

    return {
      agreementType: AgreementTypeEnum.MarketingAgreement,
      beneficiaryTitleId: currentBeneficiary.beneficiaryTitleId,
      page,
      pageSize: 50,
      serviceId: currentBeneficiary.serviceId,
      sortBy,
    };
  }, [currentBeneficiary, page, sortBy]);

  const {
    data: agreementData,
    isError,
    isLoading: isAgreementsLoading,
    isPlaceholderData,
  } = useAgreements(queryParams, {
    placeholderData: keepPreviousData,
  });

  // A failed page>1 fetch settles `agreementData` to `undefined` —
  // `keepPreviousData` only covers the pending state, not error. Falling
  // back to the last successful response keeps Prev/Next usable instead of
  // stranding the user on an empty table.
  const lastGoodAgreementData = useLastSuccessfulData(
    agreementData,
    isError,
    LAST_SUCCESSFUL_DATA_SCOPES.MARKETING_AGREEMENTS,
    currentBeneficiary,
  );

  const agreements = lastGoodAgreementData?.items || [];
  const paginationMeta = lastGoodAgreementData?.meta;

  const handlePageRequest = useCallback(
    (
      nextPage: number,
      nextSortBy: string = AGREEMENTS_DEFAULT_PARAMS.sortBy,
    ) => {
      setPage(nextPage);
      setSortBy(nextSortBy);
    },
    [],
  );

  const handleDownloadAgreementPDF = async (agreementId: string) => {
    setDownloadProps({ agreementId, isLoading: true });

    const fileName = 'Agreement.pdf';

    try {
      const agreementBlob = await downloadApi.agreement(agreementId);

      const file = `Agreement-id-${agreementId}-${fileName}`;

      downloadApi.downloadFile(agreementBlob, file);
    } catch (error) {
      // Swallowed intentionally: download errors surface via the global ErrorModal
    } finally {
      setDownloadProps({ agreementId, isLoading: false });
    }
  };

  const columns: ColumnDef<Agreement>[] = [
    {
      accessorKey: 'agreementDate',
      cell: ({ row }) =>
        row.original.agreementDate && (
          <DateFormatter dateString={row.original.agreementDate} />
        ),
      header: 'Date',
      meta: { sortKey: 'agreementDate' },
    },
    {
      accessorKey: 'agreementName',
      header: 'Agreement Name',
    },
    {
      accessorKey: 'version',
      cell: ({ row }) => toTitleCase(row.original.version),
      header: 'Version',
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => <StatusBadge type={row.original.status} />,
      header: 'Status',
    },
    {
      accessorKey: '_id',
      cell: ({ row }) => (
        <Button
          className="h-8 w-8"
          disabled={!row.original.hasAgreementPdf}
          icon={<Icon icon={DownloadIcon} weight="fill" />}
          loading={
            downloadProps?.agreementId === row.original._id &&
            downloadProps?.isLoading
          }
          size="icon"
          onClick={() => handleDownloadAgreementPDF(row.original._id)}
        >
          <span className="sr-only">Download</span>
        </Button>
      ),
      header: 'Action',
    },
  ];

  return (
    <AppDataTable<Agreement>
      columns={columns}
      data={(agreements as Agreement[]) || []}
      emptyState={{
        description:
          'There are currently no marketing agreements associated with this title.',
        title: 'No Marketing Agreements Available',
      }}
      isFetchingPage={isPlaceholderData}
      isLoading={isAgreementsLoading}
      paginationKey="marketingAgreementsTablePage"
      paginationMeta={paginationMeta}
      sortBy={sortBy}
      sortParam="marketingAgreementsTableSortBy"
      onPageRequest={handlePageRequest}
    />
  );
};
